import type { CategoryColor } from "@simplicity/ui";
import type { CSSProperties } from "react";

/**
 * Layer → category color mappings, used by both <LayerBadge> and `getLayerBadgeStyle` (inline
 * styles for controls that embed the badge hue, e.g. SIM-1689's Layer Select trigger). The
 * constants live here rather than in LayerBadge.tsx because `react-refresh/only-export-components`
 * requires a component file to export only components.
 */
export const LAYER_COLORS: Record<string, CategoryColor> = {
  "silver-canon": "slate",
  gold: "amber",
  diamond: "diamond",
};

export const LAYER_LABELS: Record<string, string> = {
  "silver-canon": "Silver",
  gold: "Gold",
  diamond: "Diamond",
};

export const DEFAULT_LAYER_COLOR: CategoryColor = "blue";

/**
 * SIM-1689: inline styles that paint a control (a Select trigger, say) in the same hue family as
 * <LayerBadge>. Matches CategoryBadge's color-mix recipe so the trigger reads as a badge without a
 * standalone <LayerBadge> element, and WCAG 2.2 AA contrast holds via the same 40/60 text mix.
 */
export function getLayerBadgeStyle(layer: string): CSSProperties {
  const color = LAYER_COLORS[layer] ?? DEFAULT_LAYER_COLOR;
  const varRef = `var(--cat-${color})`;
  return {
    backgroundColor: `color-mix(in srgb, ${varRef} 18%, transparent)`,
    color: `color-mix(in srgb, ${varRef} 40%, var(--color-foreground) 60%)`,
    borderColor: `color-mix(in srgb, ${varRef} 35%, transparent)`,
  };
}
